import React from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Text,
  VStack,
  HStack,
  Grid,
  GridItem,
  Badge,
  Divider,
  Box,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Icon,
} from "@chakra-ui/react";
import {
  FiDollarSign,
  FiCalendar,
  FiUser,
  FiTag,
  FiClock,
  FiInfo,
} from "react-icons/fi";

const ManhourRateDetailsModal = ({ isOpen, onClose, rate }) => {
  if (!rate) return null;

  // Format currency
  const formatCurrency = (amount, currency) => {
    if (!amount) return "Not set";
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
    }).format(amount);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "Not set";
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Get rate type badge color
  const getRateTypeBadgeColor = (rateType) => {
    const colors = {
      global: "blue",
      category: "green",
      activity: "purple",
      skill_level: "orange",
    };
    return colors[rateType] || "gray";
  };

  // Get skill level display name
  const getSkillLevelDisplay = (skillLevel) => {
    if (!skillLevel) return "N/A";
    const displays = {
      helper: "Helper",
      skilled: "Skilled Worker", 
      supervisor: "Supervisor",
      specialist: "Specialist",
      engineer: "Engineer",
    };
    return displays[skillLevel] || skillLevel;
  };

  // Get rate type display name
  const getRateTypeDisplay = (rateType) => {
    const displays = {
      global: "Global Rate",
      category: "Category-Specific Rate",
      activity: "Activity-Specific Rate",
      skill_level: "Skill Level Rate",
    };
    return displays[rateType] || rateType;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl" scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent
        bg="linear-gradient(145deg, #1e2936 0%, #1a202c 100%)"
        border="1px solid"
        borderColor="whiteAlpha.200"
        color="white"
      >
        <ModalHeader>
          <VStack align="start" spacing={2}>
            <HStack>
              <Icon as={FiDollarSign} color="green.400" boxSize={6} />
              <Text fontSize="2xl" fontWeight="bold">
                {rate.name}
              </Text>
            </HStack>
            <HStack>
              <Badge
                colorScheme={getRateTypeBadgeColor(rate.rateType)}
                variant="subtle"
                px={3}
                py={1}
                rounded="md"
              >
                {getRateTypeDisplay(rate.rateType)}
              </Badge>
              <Badge
                colorScheme={rate.isActive ? "green" : "red"}
                variant="subtle"
                px={3}
                py={1}
                rounded="md"
              >
                {rate.isActive ? "Active" : "Inactive"}
              </Badge>
            </HStack>
          </VStack>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody pb={6}>
          <VStack spacing={6} align="stretch">
            {/* Description */}
            {rate.description && (
              <Box>
                <HStack mb={2}>
                  <Icon as={FiInfo} color="cyan.400" />
                  <Text fontSize="md" fontWeight="semibold" color="cyan.400">
                    Description
                  </Text>
                </HStack>
                <Text color="gray.300" fontSize="sm" pl={6}>
                  {rate.description}
                </Text>
              </Box>
            )}

            <Divider />

            {/* Rate Information */}
            <Box>
              <Text fontSize="lg" fontWeight="semibold" mb={4} color="cyan.400">
                Rate Information
              </Text>
              <Grid templateColumns="repeat(2, 1fr)" gap={6}>
                <GridItem>
                  <Stat>
                    <StatLabel color="gray.400">
                      <HStack>
                        <Icon as={FiDollarSign} />
                        <Text>Hourly Rate</Text>
                      </HStack>
                    </StatLabel>
                    <StatNumber color="green.400" fontSize="2xl">
                      {formatCurrency(rate.hourlyRate, rate.currency)}
                    </StatNumber>
                    <StatHelpText color="gray.400">
                      Regular working hours
                    </StatHelpText>
                  </Stat>
                </GridItem>

                <GridItem>
                  <Stat>
                    <StatLabel color="gray.400">
                      <HStack>
                        <Icon as={FiClock} />
                        <Text>Overtime Rate</Text>
                      </HStack>
                    </StatLabel>
                    <StatNumber color="orange.400" fontSize="2xl">
                      {formatCurrency(rate.overtimeRate, rate.currency)}
                    </StatNumber>
                    <StatHelpText color="gray.400">
                      Beyond regular hours
                    </StatHelpText>
                  </Stat>
                </GridItem>

                <GridItem>
                  <Stat>
                    <StatLabel color="gray.400">
                      <HStack>
                        <Icon as={FiCalendar} />
                        <Text>Holiday Rate</Text>
                      </HStack>
                    </StatLabel>
                    <StatNumber color="purple.400" fontSize="2xl">
                      {formatCurrency(rate.holidayRate, rate.currency)}
                    </StatNumber>
                    <StatHelpText color="gray.400">
                      Holiday working hours
                    </StatHelpText>
                  </Stat>
                </GridItem>

                <GridItem>
                  <Stat>
                    <StatLabel color="gray.400">
                      <HStack>
                        <Icon as={FiTag} />
                        <Text>Priority</Text>
                      </HStack>
                    </StatLabel>
                    <StatNumber color="blue.400" fontSize="2xl">
                      {rate.priority}
                    </StatNumber>
                    <StatHelpText color="gray.400">
                      Selection priority level
                    </StatHelpText>
                  </Stat>
                </GridItem>
              </Grid>
            </Box>

            <Divider />

            {/* Scope Information */}
            <Box>
              <Text fontSize="lg" fontWeight="semibold" mb={4} color="cyan.400">
                Rate Scope
              </Text>
              <VStack spacing={4} align="stretch">
                <HStack justify="space-between">
                  <Text color="gray.400">Rate Type:</Text>
                  <Badge
                    colorScheme={getRateTypeBadgeColor(rate.rateType)}
                    variant="solid"
                    px={3}
                    py={1}
                    rounded="md"
                  >
                    {getRateTypeDisplay(rate.rateType)}
                  </Badge>
                </HStack>

                {rate.skillLevel && (
                  <HStack justify="space-between">
                    <Text color="gray.400">Skill Level:</Text>
                    <Text color="white" fontWeight="semibold">
                      {getSkillLevelDisplay(rate.skillLevel)}
                    </Text>
                  </HStack>
                )}

                {rate.masterCategory && (
                  <HStack justify="space-between">
                    <Text color="gray.400">Master Category:</Text>
                    <VStack align="end" spacing={0}>
                      <Text color="white" fontWeight="semibold">
                        {rate.masterCategory.name}
                      </Text>
                      <Text color="gray.400" fontSize="sm">
                        ({rate.masterCategory.code})
                      </Text>
                    </VStack>
                  </HStack>
                )}

                {rate.masterActivity && (
                  <HStack justify="space-between">
                    <Text color="gray.400">Master Activity:</Text>
                    <Text color="white" fontWeight="semibold">
                      {rate.masterActivity.name}
                    </Text>
                  </HStack>
                )}

                <HStack justify="space-between">
                  <Text color="gray.400">Currency:</Text>
                  <Text color="white" fontWeight="semibold">
                    {rate.currency}
                  </Text>
                </HStack>
              </VStack>
            </Box>

            <Divider />

            {/* Effective Period */}
            <Box>
              <Text fontSize="lg" fontWeight="semibold" mb={4} color="cyan.400">
                Effective Period
              </Text>
              <Grid templateColumns="repeat(2, 1fr)" gap={6}>
                <GridItem>
                  <VStack align="start" spacing={2}>
                    <Text color="gray.400" fontSize="sm">
                      Effective From
                    </Text>
                    <Text color="green.400" fontWeight="semibold" fontSize="lg">
                      {formatDate(rate.effectiveFrom)}
                    </Text>
                  </VStack>
                </GridItem>
                <GridItem>
                  <VStack align="start" spacing={2}>
                    <Text color="gray.400" fontSize="sm">
                      Effective To
                    </Text>
                    <Text 
                      color={rate.effectiveTo ? "red.400" : "gray.400"} 
                      fontWeight="semibold" 
                      fontSize="lg"
                    >
                      {rate.effectiveTo ? formatDate(rate.effectiveTo) : "Indefinite"}
                    </Text>
                  </VStack>
                </GridItem>
              </Grid>

              {/* Duration calculation */}
              {rate.effectiveFrom && (
                <Box mt={4} p={4} bg="whiteAlpha.50" rounded="md">
                  <Text color="gray.300" fontSize="sm">
                    <strong>Duration:</strong>{" "}
                    {rate.effectiveTo
                      ? `${Math.ceil(
                          (new Date(rate.effectiveTo) - new Date(rate.effectiveFrom)) /
                            (1000 * 60 * 60 * 24)
                        )} days`
                      : "Ongoing (no end date set)"}
                  </Text>
                </Box>
              )}
            </Box>

            <Divider />

            {/* Metadata */}
            <Box>
              <Text fontSize="lg" fontWeight="semibold" mb={4} color="cyan.400">
                Record Information
              </Text>
              <VStack spacing={3} align="stretch">
                {rate.creator && (
                  <HStack justify="space-between">
                    <Text color="gray.400">Created By:</Text>
                    <Text color="white">
                      {rate.creator.firstName} {rate.creator.lastName}
                    </Text>
                  </HStack>
                )}
                
                <HStack justify="space-between">
                  <Text color="gray.400">Created At:</Text>
                  <Text color="white">
                    {formatDate(rate.createdAt)}
                  </Text>
                </HStack>

                {rate.updater && (
                  <>
                    <HStack justify="space-between">
                      <Text color="gray.400">Last Updated By:</Text>
                      <Text color="white">
                        {rate.updater.firstName} {rate.updater.lastName}
                      </Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.400">Updated At:</Text>
                      <Text color="white">
                        {formatDate(rate.updatedAt)}
                      </Text>
                    </HStack>
                  </>
                )}
              </VStack>
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ManhourRateDetailsModal;
